import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { api, type CurrentUser } from '../api/client.js';
import { useRefreshSession } from '../auth/useSession.js';
import { useUploadQueue } from '../imports/UploadQueue.js';
import {
  groupOnRoute,
  groupOnToggle,
  initialGroupState,
  isGroupOpen,
  navSections,
  parseGroupOpen,
  pathInItems,
  serializeGroupOpen,
  SETTINGS_GROUP_STORAGE_KEY,
  type NavIconName,
  type NavItemSpec,
} from '../lib/nav.js';
import { Alert } from './ui.js';
import { ThemeToggle } from './ThemeToggle.js';
import { UserMenu } from './UserMenu.js';
import {
  BarcodeIcon,
  BookIcon,
  BoxIcon,
  CartIcon,
  ChartIcon,
  ChevronDownIcon,
  ClipboardIcon,
  CopyIcon,
  HomeIcon,
  MailIcon,
  MenuIcon,
  PrinterIcon,
  RestockIcon,
  SettingsIcon,
  TagIcon,
  UploadIcon,
  UsersIcon,
} from './icons.js';

/**
 * Application frame: a header with the brand and the account menu, a
 * collapsible navigation panel down the left, and the page beside it.
 *
 * Collapsing the panel leaves an icon rail rather than removing it, so the
 * navigation stays one click away on a narrow window and the page keeps its
 * position instead of jumping when the panel comes back.
 *
 * The panel lists the everyday pages (Labeling as a submenu under Products),
 * then a "Settings" group (imports, bookings, duplicates, label printing, and
 * for administrators users and email) that folds away. Account is in the profile menu at the top right, not here.
 */

const NAV_ICONS: Record<NavIconName, ReactNode> = {
  home: <HomeIcon />,
  box: <BoxIcon />,
  tag: <TagIcon />,
  restock: <RestockIcon />,
  cart: <CartIcon />,
  chart: <ChartIcon />,
  upload: <UploadIcon />,
  book: <BookIcon />,
  copy: <CopyIcon />,
  users: <UsersIcon />,
  mail: <MailIcon />,
  barcode: <BarcodeIcon />,
  printer: <PrinterIcon />,
};

const NAV_STORAGE_KEY = 'seller.nav';

function readNavOpen(): boolean {
  try {
    return localStorage.getItem(NAV_STORAGE_KEY) !== 'collapsed';
  } catch {
    return true;
  }
}

function readSettingsPreference(): boolean {
  try {
    return parseGroupOpen(localStorage.getItem(SETTINGS_GROUP_STORAGE_KEY));
  } catch {
    return false;
  }
}

export function AppShell({
  user,
  unusedRecoveryCodes,
  children,
}: {
  user: CurrentUser;
  unusedRecoveryCodes: number;
  children: ReactNode;
}) {
  const refreshSession = useRefreshSession();
  const [navOpen, setNavOpen] = useState(readNavOpen);
  const sections = navSections(user.role);

  // The SKU and Restock tables opt into the full viewport width; they carry
  // many columns and their own horizontal scroll. Every other page keeps the
  // centered, readable column, and a SKU's own detail page (/skus/:id) is not
  // a table page, so only the exact list paths widen. The Products browser is
  // centered, but its Table view (the old Families table) opts in.
  const { pathname, search } = useLocation();
  const productsTable =
    pathname === '/products' && new URLSearchParams(search).get('view') === 'table';
  const wide =
    pathname === '/skus' ||
    pathname === '/restock' ||
    productsTable ||
    pathname === '/purchase-plan';

  useEffect(() => {
    try {
      localStorage.setItem(NAV_STORAGE_KEY, navOpen ? 'open' : 'collapsed');
    } catch {
      // The preference simply will not persist; the session still honors it.
    }
  }, [navOpen]);

  // The Settings group: the owner's stored preference, plus a transient open
  // while a page inside the group is shown (so that page is visible in the
  // panel). Only the owner's click is stored; leaving the group's pages returns
  // it to the preference. The rules are in lib/nav.ts.
  const inSettings = pathInItems(pathname, sections.settings);
  const [settingsState, setSettingsState] = useState(() =>
    initialGroupState(readSettingsPreference(), inSettings),
  );
  const settingsOpen = isGroupOpen(settingsState);
  useEffect(() => {
    setSettingsState((state) => groupOnRoute(state, inSettings));
  }, [pathname, inSettings]);

  function toggleSettings() {
    const next = groupOnToggle(settingsState);
    setSettingsState(next);
    try {
      localStorage.setItem(SETTINGS_GROUP_STORAGE_KEY, serializeGroupOpen(next.preference));
    } catch {
      // As above: remembered for this session only.
    }
  }

  function signOut() {
    void api.post('/api/auth/logout').then(() => refreshSession());
  }

  return (
    <div className="flex min-h-full flex-col bg-sunken">
      <header className="flex items-center gap-3 border-b border-line bg-surface px-3 py-2">
        <button
          type="button"
          onClick={() => setNavOpen((value) => !value)}
          aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={navOpen}
          aria-controls="app-nav"
          className="rounded-md p-1.5 text-muted hover:bg-sunken hover:text-body"
        >
          <MenuIcon className="h-5 w-5" />
        </button>

        <Link to="/" className="flex items-center gap-2">
          <ClipboardIcon className="h-5 w-5 text-brand-600" />
          <span className="text-sm font-semibold tracking-tight text-strong">
            Amazon Seller App
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-3">
          <UploadIndicator />
          <ThemeToggle />
          <UserMenu user={user} onSignOut={signOut} />
        </div>
      </header>

      <div className="flex flex-1">
        <aside
          id="app-nav"
          className={`shrink-0 border-r border-line bg-surface transition-[width] duration-150 ${
            navOpen ? 'w-56' : 'w-14'
          }`}
        >
          <nav aria-label="Main" className="flex flex-col gap-0.5 p-2">
            {sections.main.map((item) => (
              <Fragment key={item.to}>
                <NavEntry item={item} expanded={navOpen} />
                {/* A page's submenu sits under it, indented; in the icon rail
                    there is no room to indent, so it is simply the next icon. */}
                {item.children && (
                  <div className={`flex flex-col gap-0.5 ${navOpen ? 'pl-3' : ''}`}>
                    {item.children.map((child) => (
                      <NavEntry key={child.to} item={child} expanded={navOpen} />
                    ))}
                  </div>
                )}
              </Fragment>
            ))}

            {navOpen ? (
              <>
                <button
                  type="button"
                  onClick={toggleSettings}
                  aria-expanded={settingsOpen}
                  aria-controls="app-nav-settings"
                  className={`mt-2 flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium hover:bg-sunken hover:text-body ${
                    inSettings && !settingsOpen ? 'text-strong' : 'text-muted'
                  }`}
                >
                  <span className="shrink-0">
                    <SettingsIcon />
                  </span>
                  <span className="truncate">Settings</span>
                  <ChevronDownIcon
                    className={`ml-auto h-4 w-4 shrink-0 transition-transform ${
                      settingsOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {/* Always rendered, so the button's aria-controls names an element
                    that exists; hidden while folded. */}
                <div
                  id="app-nav-settings"
                  hidden={!settingsOpen}
                  className={`flex-col gap-0.5 pl-3 ${settingsOpen ? 'flex' : ''}`}
                >
                  {sections.settings.map((item) => (
                    <NavEntry key={item.to} item={item} expanded />
                  ))}
                </div>
              </>
            ) : (
              // The icon rail has no room for a folding group: the Settings
              // pages are simply listed under a divider, always shown.
              <>
                <hr className="my-2 border-line" aria-hidden />
                {sections.settings.map((item) => (
                  <NavEntry key={item.to} item={item} expanded={false} />
                ))}
              </>
            )}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-6 py-6">
          <div className={wide ? '' : 'mx-auto max-w-6xl'}>
            {/* Running out of recovery codes is only a problem once it is too
                late, so the warning carries the way to fix it rather than
                leaving the reader to go looking. */}
            {unusedRecoveryCodes <= 2 && (
              <div className="mb-4">
                <Alert tone="warning">
                  {unusedRecoveryCodes === 0
                    ? 'You have no recovery codes left. '
                    : `Only ${unusedRecoveryCodes} recovery code${unusedRecoveryCodes === 1 ? '' : 's'} left. `}
                  <Link to="/account" className="font-medium underline underline-offset-2">
                    Generate a new set
                  </Link>{' '}
                  before you lose access to your authenticator.
                </Alert>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

/** A small live link to the Imports page while a batch is uploading. */
function UploadIndicator() {
  const queue = useUploadQueue();
  if (!queue.running || !queue.progress) return null;
  return (
    <Link
      to="/imports"
      className="flex items-center gap-2 rounded-md bg-sunken px-2.5 py-1 text-xs font-medium text-body hover:bg-raised"
    >
      <span
        className="h-3 w-3 animate-spin rounded-full border-2 border-line-strong border-t-body"
        aria-hidden
      />
      Uploading {queue.progress.current} of {queue.progress.total}
    </Link>
  );
}

function NavEntry({ item, expanded }: { item: NavItemSpec; expanded: boolean }) {
  // A page with a submenu is not lit while one of its submenu pages is shown:
  // that page's own entry, right under it, is.
  const { pathname } = useLocation();
  const inChild = item.children ? pathInItems(pathname, item.children) : false;
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium ${
      isActive && !inChild ? 'bg-raised text-strong' : 'text-muted hover:bg-sunken hover:text-body'
    }`;

  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      // With the labels hidden, the name still has to surface somewhere.
      {...(expanded ? {} : { title: item.label, 'aria-label': item.label })}
      className={linkClass}
    >
      <span className="shrink-0">{NAV_ICONS[item.icon]}</span>
      {expanded && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}
